/* eslint-disable max-lines */
import { WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import { BottomSheet, type UseModalState, useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { useGetClipboardScoreHistory } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreHistory";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import {
  useDiscardLatenessBooktimeThreshold,
  useDynamicPenaltiesConfig,
} from "@src/appV2/ClipboardScore/hooks/featureFlags";
import { useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useCountdownTimer } from "@src/appV2/lib/utils/useCountdownTimer";
import { RestrictedBottomSheet } from "@src/appV2/redesign/ClipboardScore/components/BottomSheets/RestrictedBottomSheet";
import { ClipboardScoreActions } from "@src/appV2/redesign/ClipboardScore/utils/constants";
import {
  getConsequence,
  isWorkerInviteRequired,
} from "@src/appV2/redesign/ClipboardScore/utils/utils";
import { Button } from "@src/appV2/redesign/components/Button";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { formatDurationRangeLabel } from "@src/appV2/redesign/utils/formatDurationLabel";
import { useCancelShift } from "@src/appV2/Shifts/Shift/ShiftState/api/useCancelShift";
import { useSupportLinks } from "@src/appV2/support/hooks/useSupportLinks";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useZendeskArticle } from "@src/appV2/Zendesk/useZendeskArticle";
import { differenceInHours, parseISO } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { formatInTimeZone } from "date-fns-tz";
import { useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { DialogFooter } from "../../../components/DialogFooter";
import { useWorkerBookingsData } from "../../hooks/useWorkerBookingsData";
import { MY_SHIFTS_FULL_PATH } from "../../path";
import { useGetShiftDetailsData } from "../api/useGetShiftDetailsData";
import { CancelShiftContent } from "./CancelShiftContent";
import { useClipboardScoreData } from "./hooks/useClipboardScoreData";
import { ShiftCancelledWithClipboardScoreBottomSheet } from "./ShiftCancelledWithClipboardScoreBottomSheet";
import { ShiftCancelledWithoutClipboardScoreBottomSheet } from "./ShiftCancelledWithoutClipboardScoreBottomSheet";

export interface CancelShiftBottomSheetProps {
  modalState: UseModalState;
  isNoCallNoShow: boolean;
  shiftStartDate: Date;
  shiftId: string;
  currentDate: Date;
  isTriggeredFromNudge?: boolean;
  isOrientationBlockShift?: boolean;
  onShiftCancelled?: () => void;
}

export const CANCEL_SHIFT_COUNTDOWN_DURATION_IN_SECONDS = 3;

// eslint-disable-next-line complexity
export function CancelShiftBottomSheet(props: CancelShiftBottomSheetProps) {
  const {
    modalState,
    isNoCallNoShow,
    shiftStartDate,
    shiftId,
    currentDate,
    isTriggeredFromNudge,
    isOrientationBlockShift = false,
    onShiftCancelled,
  } = props;

  const shiftCancelledConfirmationModalState = useModalState();
  const restrictedScreenModalState = useModalState();
  const history = useHistory();

  const worker = useDefinedWorker();

  const { mutateAsync: cancelShift, isLoading: isLoadingCancelShift } = useCancelShift();

  const { showErrorToast } = useToast();
  const { openArticle } = useZendeskArticle();
  const { attendanceScoreSupportLink } = useSupportLinks();

  const shiftCancellationLeadTimeInHours = differenceInHours(shiftStartDate, currentDate, {
    roundingMethod: "ceil",
  });

  const dynamicPenaltiesConfig = useDynamicPenaltiesConfig();
  const isDynamicPenaltiesEnabled = dynamicPenaltiesConfig.enabled;
  const maximumShiftsToCheckForCancellationPenalty = isDynamicPenaltiesEnabled
    ? dynamicPenaltiesConfig.cancellationPenalty.maximumShiftsToCheck
    : undefined;

  const { shift } = useGetShiftDetailsData({
    shiftId,
  });

  const {
    data: clipboardScoreProfile,
    isSuccess: clipboardScoreProfileSuccess,
    isLoading: clipboardScoreProfileLoading,
  } = useGetClipboardScoreProfile(
    worker.userId,
    {
      enabled: modalState.modalIsOpen,
    },
    shift?.agentReq,
    shift?.facilityId
  );

  const { data: clipboardScoreHistory } = useGetClipboardScoreHistory(
    { userId: worker.userId },
    { enabled: isDefined(worker.userId) && modalState.modalIsOpen }
  );
  const { discardLatenessBooktimeThresholdInHours } = useDiscardLatenessBooktimeThreshold();

  const { bookings } = useWorkerBookingsData();
  const futureShiftsCount = bookings.filter(
    (booking) => booking.type === "shift" && booking.data.shiftId !== shiftId
  ).length;

  const { ranking, isScoreFrozen } = useClipboardScoreData({
    clipboardScoreHistory,
    clipboardScoreProfile,
    shift,
    discardLatenessBooktimeThresholdInHours,
  });

  const { startTimer, secondsLeft, resetTimer } = useCountdownTimer({
    durationInSeconds: CANCEL_SHIFT_COUNTDOWN_DURATION_IN_SECONDS,
  });

  useEffect(() => {
    if (modalState.modalIsOpen) {
      startTimer();
    } else {
      resetTimer();
    }
  }, [modalState.modalIsOpen, resetTimer, startTimer]);

  async function handleCancelShift() {
    try {
      await cancelShift({
        shiftId,
        reasonType: "NONE",
        reasonDescription: "",
        isNative: false,
      });

      modalState.closeModal();
      shiftCancelledConfirmationModalState.openModal();

      if (isTriggeredFromNudge) {
        logEvent(APP_V2_USER_EVENTS.ATTENDANCE_CONFIRMATION_NUDGE_CANCELLED, {
          workerId: worker.userId,
          shiftId,
        });
      }

      logEvent(APP_V2_APP_EVENTS.CANCEL_SHIFT_SUCCESS, {
        shiftId,
      });
    } catch {
      showErrorToast("Something went wrong while cancelling your shift. Please try again.");
    }
  }

  const cancelShiftPolicy = clipboardScoreProfileSuccess
    ? clipboardScoreProfile.policy?.cancelShift?.find((policy) => {
        const [minHours, maxHours] = policy.leadTime;
        return (
          (isDefined(minHours) ? shiftCancellationLeadTimeInHours >= minHours : true) &&
          (isDefined(maxHours) ? shiftCancellationLeadTimeInHours < maxHours : true)
        );
      })
    : undefined;

  const noCallNoShowPolicy = clipboardScoreProfileSuccess
    ? clipboardScoreProfile.policy?.noCallNoShow
    : undefined;

  const scoreChange = isNoCallNoShow ? noCallNoShowPolicy?.points : cancelShiftPolicy?.points;

  const newClipboardScore = useMemo(() => {
    if (!clipboardScoreProfileSuccess) {
      return undefined;
    }

    if (isNoCallNoShow && isDefined(scoreChange) && scoreChange < 0) {
      return 0;
    }

    return (clipboardScoreProfile.clipboardScore ?? 0) + (scoreChange ?? 0);
  }, [clipboardScoreProfile, scoreChange, clipboardScoreProfileSuccess, isNoCallNoShow]);

  function getPolicyTitle() {
    if (isNoCallNoShow) {
      return "No show";
    }

    const [from, to] = cancelShiftPolicy?.leadTime ?? [0, undefined];

    return `${formatDurationRangeLabel(from ?? 0, to ?? undefined, "hour", {
      format: "abbreviated",
    })} cancellation`;
  }

  function getButtonText(isOrientationBlockShift: boolean) {
    if (isOrientationBlockShift) {
      return "Cancel entire block";
    }

    return "Cancel shift";
  }

  const policyConsequence = getConsequence({
    action: isNoCallNoShow
      ? ClipboardScoreActions.NO_CALL_NO_SHOW
      : ClipboardScoreActions.CANCEL_SHIFT,
    score: clipboardScoreProfile?.clipboardScore ?? 0,
    adjustment: scoreChange ?? 0,
  });

  const reEnrollmentDate = clipboardScoreProfile?.accountStatus?.reEnrollmentDate;
  const reEnrollmentScore = clipboardScoreProfile?.restriction?.reEnrollmentScore;

  const isFinalRestriction =
    clipboardScoreProfile?.accountStatus.restrictionDuration ===
    clipboardScoreProfile?.policy?.finalRestriction?.duration;

  const newlyRestricted =
    (newClipboardScore ?? 0) <= 0 && (newClipboardScore ?? 0) - (scoreChange ?? 0) > 0;

  const handlePenaltyExplanationClick = async () => {
    await openArticle(attendanceScoreSupportLink);
  };

  const handleShiftCancelledConfirmation = () => {
    // Call the callback when ShiftCancelledConfirmationBottomSheet closes
    if (onShiftCancelled) {
      onShiftCancelled();
    } else if (newlyRestricted) {
      shiftCancelledConfirmationModalState.closeModal();
      restrictedScreenModalState.openModal();
    } else {
      // If no callback, navigate to bookings
      history.push(MY_SHIFTS_FULL_PATH);
    }
  };

  const isInviteRequired = isWorkerInviteRequired(worker);

  return (
    <>
      <BottomSheet modalState={modalState}>
        <CancelShiftContent
          isLoading={clipboardScoreProfileLoading}
          isRestricted={clipboardScoreProfile?.accountStatus.status === WorkerStage.RESTRICTED}
          isInviteRequired={isInviteRequired}
          isDynamicPenaltiesEnabled={isDynamicPenaltiesEnabled}
          maximumShiftsToCheckForCancellationPenalty={maximumShiftsToCheckForCancellationPenalty}
          currentClipboardScore={clipboardScoreProfile?.clipboardScore}
          newClipboardScore={newClipboardScore}
          multiplier={clipboardScoreProfile?.clipboardScorePenaltyMultipliers?.cancellation.next}
          futureShiftsCount={futureShiftsCount}
          nextRestrictionDuration={clipboardScoreProfile?.restriction?.nextDuration ?? 7}
          policyToBeApplied={{
            label: getPolicyTitle(),
            consequence: policyConsequence,
          }}
          scoreChange={scoreChange}
          isOrientationBlockShift={isOrientationBlockShift}
          onPenaltyExplanationClick={handlePenaltyExplanationClick}
        />
        <DialogFooter orientation="vertical" sx={{ marginTop: 10 }}>
          <LoadingButton
            fullWidth
            variant="outlined"
            size="large"
            sx={{
              color: (theme) => theme.palette.error.light,
              borderColor: (theme) => theme.palette.border?.primary,
            }}
            isLoading={isLoadingCancelShift || clipboardScoreProfileLoading}
            disabled={clipboardScoreProfileLoading || secondsLeft > 0}
            onClick={async () => {
              await handleCancelShift();
            }}
          >
            {secondsLeft > 0
              ? `Proceed in ${secondsLeft} seconds`
              : getButtonText(isOrientationBlockShift)}
          </LoadingButton>
          <Button
            fullWidth
            variant="outlined"
            size="large"
            sx={{
              color: (theme) => theme.palette.primary.dark,
            }}
            onClick={() => {
              modalState.closeModal();
            }}
          >
            Don&apos;t cancel
          </Button>
        </DialogFooter>
      </BottomSheet>
      {isScoreFrozen ? (
        <ShiftCancelledWithoutClipboardScoreBottomSheet
          modalState={shiftCancelledConfirmationModalState}
          onContinue={handleShiftCancelledConfirmation}
        />
      ) : (
        <ShiftCancelledWithClipboardScoreBottomSheet
          modalState={shiftCancelledConfirmationModalState}
          scoreChange={scoreChange ?? 0}
          currentScore={newClipboardScore ?? 0}
          ranking={ranking}
          isTriggeredFromNudge={isTriggeredFromNudge}
          onContinue={handleShiftCancelledConfirmation}
        />
      )}
      <RestrictedBottomSheet
        modalState={restrictedScreenModalState}
        isFinalRestriction={isFinalRestriction}
        liftTime={
          reEnrollmentDate &&
          formatInTimeZone(parseISO(reEnrollmentDate), worker.tmz, "MMMM d, yyyy")
        }
        reEnrollmentScore={reEnrollmentScore ?? 80}
        onContinue={() => {
          history.push(MY_SHIFTS_FULL_PATH);
        }}
      />
    </>
  );
}
/* eslint-enable max-lines */
